import React, { useEffect, useMemo, useState } from 'react';
import {
  Check,
  Plus,
  Trash2,
  Pencil,
  Save,
  X,
  ExternalLink,
  Copy,
  UserPlus,
  Sparkles,
  Loader,
} from 'lucide-react';
import type { AIAnalysis, GuideComponent, GuideComponentKey, GuideItem } from '../types';
import { supabase, TABLES } from '../api/db';
import {
  GUIDE_COMPONENT_META,
  normalizeGuideComponents,
  guideProgress,
} from '../constants/guideFramework';
import { getAIAnalysisService } from '../services/ai/aiAnalysisService';

interface Member {
  id: string;
  name: string;
}

interface Props {
  taskId: string;
  analysis: AIAnalysis;
  canEdit: boolean;
  /** Displayed as the source badge ('AI pre-filled' vs 'Self-authored'). */
  onSaved?: () => void;
}

function makeItem(): GuideItem {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `item-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
  return { id, title: '', body: '' };
}

// ── Progress ring ─────────────────────────────────────────────────────────────
const ProgressRing: React.FC<{ pct: number; size?: number }> = ({ pct, size = 88 }) => {
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="currentColor" strokeWidth={stroke} fill="none" className="text-tech-text/10" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#guideGrad)"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(0.22,1,0.36,1)' }}
        />
        <defs>
          <linearGradient id="guideGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-tech-text leading-none">{pct}%</span>
      </div>
    </div>
  );
};

// ── One item row ──────────────────────────────────────────────────────────────
const ItemRow: React.FC<{
  compKey: GuideComponentKey;
  item: GuideItem;
  editing: boolean;
  accent: string;
  members: Member[];
  onChange: (patch: Partial<GuideItem>) => void;
  onDelete: () => void;
}> = ({ compKey, item, editing, accent, members, onChange, onDelete }) => {
  const meta = GUIDE_COMPONENT_META[compKey];
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.body || item.title);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  if (editing) {
    return (
      <div className="rounded-2xl border border-tech-text/10 bg-tech-dark/40 p-3 space-y-2">
        <div className="flex items-start gap-2">
          <input
            value={item.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder={`${meta.itemLabel} title`}
            className="input-tech flex-1 text-sm font-semibold"
          />
          <button onClick={onDelete} className="p-2 rounded-lg text-tech-text-muted hover:text-rose-400 hover:bg-rose-500/10 transition" title="Remove">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <textarea
          value={item.body}
          onChange={(e) => onChange({ body: e.target.value })}
          placeholder={compKey === 'prompts' ? 'The full, copy-pasteable prompt…' : 'Details…'}
          rows={compKey === 'prompts' ? 4 : 2}
          className="input-tech w-full text-sm resize-y"
        />
        <div className="flex flex-wrap gap-2">
          {meta.linky && (
            <input
              value={item.url ?? ''}
              onChange={(e) => onChange({ url: e.target.value })}
              placeholder="https://… (optional link)"
              className="input-tech flex-1 min-w-[180px] text-xs"
            />
          )}
          <input
            value={item.tag ?? ''}
            onChange={(e) => onChange({ tag: e.target.value })}
            placeholder="tag (optional)"
            className="input-tech w-32 text-xs"
          />
          {meta.checklist && members.length > 0 && (
            <select
              value={item.assignee ?? ''}
              onChange={(e) => {
                const m = members.find((x) => x.id === e.target.value);
                onChange({ assignee: m?.id, assigneeName: m?.name });
              }}
              className="input-tech text-xs"
            >
              <option value="">Unassigned</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    );
  }

  // ── read view ──
  return (
    <div className="group rounded-2xl border border-tech-text/[0.06] bg-tech-dark/30 p-4 hover:border-tech-text/15 transition">
      <div className="flex items-start gap-3">
        {meta.checklist && (
          <button
            onClick={() => onChange({ done: !item.done })}
            className="mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition"
            style={{
              borderColor: item.done ? accent : 'rgba(148,163,184,0.4)',
              background: item.done ? accent : 'transparent',
            }}
            title={item.done ? 'Mark not done' : 'Mark done'}
          >
            {item.done && <Check className="w-3.5 h-3.5 text-tech-dark" strokeWidth={3} />}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className={`font-semibold text-tech-text hover:underline inline-flex items-center gap-1 ${item.done ? 'line-through opacity-50' : ''}`}
              >
                {item.title || item.url}
                <ExternalLink className="w-3.5 h-3.5 opacity-60" />
              </a>
            ) : (
              <span className={`font-semibold text-tech-text ${item.done ? 'line-through opacity-50' : ''}`}>
                {item.title}
              </span>
            )}
            {item.tag && (
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ color: accent, background: `${accent}1a` }}>
                {item.tag}
              </span>
            )}
            {item.assigneeName && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-tech-text/10 text-tech-text-muted inline-flex items-center gap-1">
                <UserPlus className="w-3 h-3" />
                {item.assigneeName}
              </span>
            )}
          </div>
          {item.body && (
            compKey === 'prompts' ? (
              <div className="relative mt-2">
                <pre className="whitespace-pre-wrap text-xs text-tech-text/90 font-mono bg-tech-dark/60 border border-tech-text/10 rounded-xl p-3 pr-10">{item.body}</pre>
                <button onClick={copy} className="absolute top-2 right-2 p-1.5 rounded-lg bg-tech-text/10 hover:bg-tech-text/20 text-tech-text-muted transition" title="Copy prompt">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            ) : (
              <p className="mt-1 text-sm text-tech-text-muted leading-relaxed whitespace-pre-wrap">{item.body}</p>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// ── Full framework ────────────────────────────────────────────────────────────
const GuideFramework: React.FC<Props> = ({ taskId, analysis, canEdit, onSaved }) => {
  const [components, setComponents] = useState<GuideComponent[]>(() =>
    normalizeGuideComponents(analysis.guideComponents)
  );
  const [summary, setSummary] = useState(analysis.summary || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const isAiFilled =
    (analysis as unknown as { source?: string }).source === '3rdeyefeel-paid' ||
    (analysis.aiProvider === 'anthropic' && analysis.tokensUsed > 0);

  // Re-sync when a fresh analysis arrives (realtime) and we're not mid-edit.
  useEffect(() => {
    if (editing) return;
    setComponents(normalizeGuideComponents(analysis.guideComponents));
    setSummary(analysis.summary || '');
  }, [analysis, editing]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase
        .from(TABLES.TASK_PARTICIPANTS)
        .select('user_id, display_name')
        .eq('task_id', taskId);
      if (active && Array.isArray(data)) {
        setMembers(
          data.map((r: Record<string, unknown>) => ({
            id: String(r.user_id),
            name: String(r.display_name || 'Member'),
          }))
        );
      }
    })();
    return () => {
      active = false;
    };
  }, [taskId]);

  const progress = useMemo(() => guideProgress(components), [components]);

  const mutateItem = (ci: number, ii: number, patch: Partial<GuideItem>) =>
    setComponents((prev) =>
      prev.map((comp, x) =>
        x === ci ? { ...comp, items: comp.items.map((it, y) => (y === ii ? { ...it, ...patch } : it)) } : comp
      )
    );

  const addItem = (ci: number) =>
    setComponents((prev) => prev.map((comp, x) => (x === ci ? { ...comp, items: [...comp.items, makeItem()] } : comp)));

  const deleteItem = (ci: number, ii: number) =>
    setComponents((prev) => prev.map((comp, x) => (x === ci ? { ...comp, items: comp.items.filter((_, y) => y !== ii) } : comp)));

  const setIntro = (ci: number, intro: string) =>
    setComponents((prev) => prev.map((comp, x) => (x === ci ? { ...comp, intro } : comp)));

  // Toggling a checklist item (progress) autosaves even outside edit mode.
  const toggleDone = async (ci: number, ii: number, patch: Partial<GuideItem>) => {
    const next = components.map((comp, x) =>
      x === ci ? { ...comp, items: comp.items.map((it, y) => (y === ii ? { ...it, ...patch } : it)) } : comp
    );
    setComponents(next);
    if (!editing) await getAIAnalysisService().saveGuide(taskId, { components: next });
  };

  const save = async () => {
    setSaving(true);
    const ok = await getAIAnalysisService().saveGuide(taskId, { components, summary });
    setSaving(false);
    if (ok) {
      setEditing(false);
      onSaved?.();
    }
  };

  const cancel = () => {
    setComponents(normalizeGuideComponents(analysis.guideComponents));
    setSummary(analysis.summary || '');
    setEditing(false);
  };

  const scrollTo = (key: string) => document.getElementById(`guide-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="rounded-3xl p-5 md:p-6 bg-gradient-to-br from-tech-cyan/10 via-transparent to-tech-accent/10 border border-tech-cyan/20">
        <div className="flex items-start gap-5">
          <ProgressRing pct={progress.pct} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-tech-cyan inline-flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Implementation Guide
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-tech-text/10 text-tech-text-muted">
                {isAiFilled ? 'AI pre-filled' : 'Self-authored'}
              </span>
            </div>
            {editing ? (
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="One or two sentences on what this guide gets you to…"
                rows={2}
                className="input-tech w-full text-sm"
              />
            ) : (
              <p className="text-tech-text-muted text-sm leading-relaxed">
                {summary || 'No overview yet. Work through the ten components below to build out your plan.'}
              </p>
            )}
            <p className="text-xs text-tech-text-muted/70 mt-2">
              {progress.total > 0 ? `${progress.done} of ${progress.total} build steps done` : 'Add build steps to track progress'}
            </p>
          </div>
          {canEdit && (
            <div className="shrink-0">
              {editing ? (
                <div className="flex gap-2">
                  <button onClick={save} disabled={saving} className="border border-tech-cyan/50 text-tech-cyan bg-tech-cyan/10 hover:bg-tech-cyan/20 font-semibold transition-colors inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl">
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                  </button>
                  <button onClick={cancel} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl border border-tech-text/15 text-tech-text-muted hover:text-tech-text transition">
                    <X className="w-4 h-4" /> Cancel
                  </button>
                </div>
              ) : (
                <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl border border-tech-cyan/30 text-tech-cyan hover:bg-tech-cyan/10 transition">
                  <Pencil className="w-4 h-4" /> Edit guide
                </button>
              )}
            </div>
          )}
        </div>

        {/* Component quick-nav */}
        <div className="flex gap-2 overflow-x-auto mt-5 pb-1 custom-scrollbar">
          {components.map((comp) => {
            const meta = GUIDE_COMPONENT_META[comp.key];
            const Icon = meta.icon;
            return (
              <button
                key={comp.key}
                onClick={() => scrollTo(comp.key)}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-tech-text/10 bg-tech-dark/40 text-tech-text-muted hover:text-tech-text hover:border-tech-text/25 transition"
              >
                <Icon className="w-3.5 h-3.5" style={{ color: meta.accent }} />
                {meta.label}
                {comp.items.length > 0 && <span className="text-[10px] opacity-60">{comp.items.length}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Components */}
      {components.map((comp, ci) => {
        const meta = GUIDE_COMPONENT_META[comp.key];
        const Icon = meta.icon;
        const empty = comp.items.length === 0 && !comp.intro;
        return (
          <section
            key={comp.key}
            id={`guide-${comp.key}`}
            className="scroll-mt-24 rounded-3xl border bg-tech-dark/20 p-5 md:p-6"
            style={{ borderColor: `${meta.accent}26` }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${meta.accent}1f` }}>
                <Icon className="w-5 h-5" style={{ color: meta.accent }} />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-tech-text leading-tight">{meta.label}</h3>
                <p className="text-xs text-tech-text-muted">{meta.blurb}</p>
              </div>
            </div>

            {editing ? (
              <textarea
                value={comp.intro}
                onChange={(e) => setIntro(ci, e.target.value)}
                placeholder="Optional intro for this section…"
                rows={2}
                className="input-tech w-full text-sm mb-3"
              />
            ) : (
              comp.intro && <p className="text-sm text-tech-text-muted mb-3">{comp.intro}</p>
            )}

            {empty && !editing ? (
              <div className="text-sm text-tech-text-muted/60 italic py-3">
                {canEdit ? `Nothing here yet — hit Edit guide to add ${meta.itemLabel.toLowerCase()}s.` : 'Nothing here yet.'}
              </div>
            ) : (
              <div className="space-y-2.5">
                {comp.items.map((item, ii) => (
                  <ItemRow
                    key={item.id}
                    compKey={comp.key}
                    item={item}
                    editing={editing}
                    accent={meta.accent}
                    members={members}
                    onChange={(patch) =>
                      meta.checklist && 'done' in patch && !editing
                        ? void toggleDone(ci, ii, patch)
                        : mutateItem(ci, ii, patch)
                    }
                    onDelete={() => deleteItem(ci, ii)}
                  />
                ))}
              </div>
            )}

            {editing && (
              <button
                onClick={() => addItem(ci)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-xl border border-dashed border-tech-text/20 text-tech-text-muted hover:text-tech-text hover:border-tech-text/40 transition"
              >
                <Plus className="w-4 h-4" /> Add {meta.itemLabel.toLowerCase()}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default GuideFramework;
